import { useCallback, useEffect } from 'react'

import { useRouter } from 'next/router'

import { Box, Loading } from '@fe-toolkit/web-components/ssr'

import { customEvent } from '@fe-toolkit/eventing'

import { ContractType } from '@giffgaff/broadband-common'

import { EQueryKeys } from '@/constants'

import { useMemberContext } from '@/context/MemberContext'
import { useCreatePlanBuyoutClaim } from '@/hooks/apiHooks'
import useApiHook from '@/hooks/useApiHook'
import useGetPlanWithAmendments from '@/hooks/useGetPlanWithAmendments'
import { PageHead } from '@/layouts/PageHead'

import { useEffectOnceWithCondition } from '@/hooks/useEffectOnceWithCondition'
import { anyQueriesLoading } from '@/utils/anyQueriesLoading'

import Profile from './Profile'

const ProfileContainer = () => {
  const {
    useGetProfileOverview,
    useGetECommerceOrderSchedule,
    useGetOrchestratedPayments,
    useGetMemberCardDetails,
    useGetPlanBuyoutDetails,
  } = useApiHook()
  const { data, ...memberQuery } = useMemberContext()
  const memberId = data?.data?.id ? `${data?.data?.id}` : undefined
  const router = useRouter()

  const profileOverview = useGetProfileOverview(
    { memberId },
    {
      enabled: !!memberId,
      queryKey: [EQueryKeys.PROFILE, memberId],
    },
  )

  const { plan, order } = profileOverview.data ?? {}

  useEffect(() => {
    const { contractInformationDocId, contractSummaryDocId } = plan ?? {}

    if (plan && (!contractSummaryDocId || !contractInformationDocId)) {
      const interval = setInterval(() => {
        profileOverview.refetch()
      }, 10000)

      return () => clearInterval(interval)
    }
  }, [plan, profileOverview])

  const { ecommerceOrderScheduleId, id: planId } = plan ?? {}

  const scheduleQueryEnabled = !!ecommerceOrderScheduleId

  const ecommerceOrderSchedule = useGetECommerceOrderSchedule(
    { scheduleId: ecommerceOrderScheduleId },
    {
      enabled: scheduleQueryEnabled,
      queryKey: [EQueryKeys.ECOMMERCE_ORDER_SCHEDULE, ecommerceOrderScheduleId],
    },
  )

  const planBuyoutDetails = useGetPlanBuyoutDetails(
    { planId },
    {
      enabled: !!planId,
      queryKey: [EQueryKeys.PLAN_BUYOUT_DETAILS, planId],
    },
  )

  const {
    mutate: createPlanBuyoutClaim,
    isPending: isCreatingBuyoutClaim,
    isSuccess: hasCreatedBuyoutClaim,
  } = useCreatePlanBuyoutClaim({
    onSuccess: ({ data: buyoutClaim }) => {
      router.push(`claim/${buyoutClaim.evidenceSession.id}`)
    },
  })

  const isCreatingEvidenceSession = isCreatingBuyoutClaim || hasCreatedBuyoutClaim

  const handleUploadExitBillClick = useCallback(() => {
    const evidenceSessionId = planBuyoutDetails.data?.evidenceSessionId

    if (evidenceSessionId) {
      router.push(`claim/${evidenceSessionId}`)

      return
    }

    if (planId) {
      createPlanBuyoutClaim({ planId })
    }
  }, [createPlanBuyoutClaim, planBuyoutDetails.data?.evidenceSessionId, planId, router])

  // Note: this currently assumes a member only has one broadband order
  const orchestratedPaymentsResponse = useGetOrchestratedPayments(
    {
      memberId,
      sort: ['-updated'],
      'domain.in': ['BROADBAND'],
    },
    {
      enabled: !!memberId && !!ecommerceOrderScheduleId,
      queryKey: ['orchestratedPayments', '-updated', 'BROADBAND', memberId, ecommerceOrderScheduleId],
    },
  )

  const orchestratedPayments = orchestratedPaymentsResponse?.data?.data ?? []

  const cardDetailsResponse = useGetMemberCardDetails(
    {},
    { queryKey: ['member_card_details', memberId], enabled: !!memberId },
  )

  const { amendmentDetails, newPlan } = useGetPlanWithAmendments(planId)

  const handleDownloadContract = useCallback(
    (contractDocId: string, contractType: ContractType) => {
      if (!planId) {
        return
      }

      window.open(
        `/profile/broadband/api/documents/download-contract?planId=${planId}&contractId=${contractDocId}&contractType=${contractType}`,
        '_blank',
      )
    },
    [planId],
  )

  useEffectOnceWithCondition(
    ([_1, _2, order, plan]) => {
      customEvent('bb_dashboard_impression', {
        orderId: order?.id ?? null,
        planId: plan?.id ?? null,
        contractId: plan?.contractInformationDocId ?? null,
        planStatus: plan?.active === true ? 'active' : 'inactive',
        orderStatus: order?.state ?? null,
      })
    },
    ([hasData, hasError]) => {
      return !!(hasData || hasError)
    },
    [profileOverview.isFetched, profileOverview.isError, order, plan] as const,
  )

  const isLoading = anyQueriesLoading([
    memberQuery,
    profileOverview,
    ecommerceOrderSchedule,
    orchestratedPaymentsResponse,
    cardDetailsResponse,
    amendmentDetails,
    newPlan,
  ])

  return (
    <>
      <PageHead title="Profile" />
      {isLoading ? (
        <Box
          sx={({ theme }) => ({
            backgroundColor: theme.colors.wildSand,
            padding: '1px',
          })}
        >
          <Box
            sx={({ theme }) => ({
              maxWidth: theme.layout.maxWidth,
              margin: `0 auto`,
              height: '50vh',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              padding: theme.spacing.small,
              [theme.breakpoints.up('medium')]: {
                paddingTop: theme.spacing.medium,
              },
            })}
          >
            <Loading />
          </Box>
        </Box>
      ) : (
        <Profile
          amendments={{ details: amendmentDetails.data, newPlan: newPlan.data }}
          buyoutDetails={planBuyoutDetails.data}
          cardDetails={cardDetailsResponse.data?.length ? cardDetailsResponse.data[0] : undefined}
          ecommerceOrderSchedule={ecommerceOrderSchedule.data}
          handleDownloadContract={handleDownloadContract}
          isCreatingEvidenceSession={isCreatingEvidenceSession}
          memberData={data?.data}
          orchestratedPayments={orchestratedPayments}
          profileOverview={profileOverview?.data}
          onUploadExitBillClick={handleUploadExitBillClick}
        />
      )}
    </>
  )
}

export default ProfileContainer
